/**
 * numeroColombiano — parser y formateador UNICOS para cifras en formato
 * colombiano (OCR-T1, bitacora #5260, diagnostico #5258).
 *
 * El bug que mata: inputs type="number" + Number() pelado. La tablet con
 * locale es-CO escribe "1.234,56", el browser sanitiza el value a cadena
 * VACIA, Number("") da 0 y el submit guarda un CERO que el usuario nunca
 * escribio ("ves un numero y se guarda otro"). En el kiosko, el replace de
 * una sola coma convertia "1.234,56" en "1.234.56" = NaN.
 *
 * Regla de oro: texto invalido JAMAS se convierte en 0 ni en null silencioso.
 * El que llama distingue: '' -> null (sin valor), invalido -> NaN (rechazar
 * con mensaje), valido -> number.
 *
 * @module domain/value-objects/numeroColombiano
 */

/**
 * Parsea una cifra en formato colombiano (o canonico) a number.
 *
 * Casos: "1.234,56" -> 1234.56 · "47.800" -> 47800 (punto = miles cuando el
 * grupo final tiene 3 digitos y hay grupos antes) · "47.8" -> 47.8 ·
 * "47,5" -> 47.5 · "1234.56" -> 1234.56 · "$ 1.234" -> 1234 ·
 * "" -> null · "abc" -> NaN.
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null|NaN} null = vacio (sin valor); NaN = invalido (rechazar)
 */
export function parsearNumeroColombiano(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return isNaN(raw) ? NaN : raw;
  let s = String(raw).trim();
  if (s === '') return null;
  // Tolerar simbolo de pesos y espacios internos de miles ("$ 1 234")
  s = s.replace(/[$\s]/g, '');
  if (s === '' || s === '-' ) return NaN;
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  if (!/^[\d.,]+$/.test(s)) return NaN;

  const tieneComa = s.includes(',');
  const tienePunto = s.includes('.');
  let canonico;
  if (tieneComa && tienePunto) {
    // Formato colombiano completo: puntos = miles, coma = decimal.
    canonico = s.replace(/\./g, '').replace(',', '.');
  } else if (tieneComa) {
    // Solo coma: decimal ("47,5") o miles con coma no se usa en CO -> decimal.
    // Varias comas ("1,234,567") = miles estilo US: quitarlas.
    canonico = (s.match(/,/g) || []).length > 1 ? s.replace(/,/g, '') : s.replace(',', '.');
  } else if (tienePunto) {
    const partes = s.split('.');
    const grupoFinal = partes[partes.length - 1];
    // Punto = miles cuando el grupo final tiene EXACTAMENTE 3 digitos y hay
    // digitos antes ("47.800", "1.234.567"). Con 1-2 digitos finales es
    // decimal ("47.8", "1234.56"). Con >1 punto siempre miles.
    if (partes.length > 2 || (grupoFinal.length === 3 && partes[0].length > 0)) {
      canonico = s.replace(/\./g, '');
    } else {
      canonico = s;
    }
  } else {
    canonico = s;
  }
  const n = Number(canonico);
  if (isNaN(n)) return NaN;
  return neg ? -n : n;
}

/**
 * Formatea en es-CO. Por defecto esconde decimales SOLO si el valor es entero;
 * si tiene fraccion, muestra 2 decimales SIEMPRE — el criterio del ASSIGN
 * #5260: nadie aprueba una cifra sin poder ver por que no cuadra. Los
 * centavos aparecen exactamente donde existen.
 *
 * @param {number|null|undefined} n
 * @param {{ forzarCentavos?: boolean }} [opts] forzarCentavos: 2 decimales siempre
 * @returns {string}
 */
export function formatearCOP(n, opts = {}) {
  const v = Number(n);
  if (n === null || n === undefined || isNaN(v)) return '—';
  const esEntero = Number.isInteger(v);
  const decimales = (opts.forzarCentavos || !esEntero) ? 2 : 0;
  return v.toLocaleString('es-CO', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/**
 * Formatea un numero que NO es dinero (cantidades, porcentajes, conteos) con las MISMAS opciones de
 * Number.prototype.toLocaleString (contrato de vista, regla numero_a_mano, Ares #42423). Pasar una vista de
 * `x.toLocaleString('es-CO', o)` a `formatearNumero(x, o)` no cambia nada de lo que se ve; lo que cambia es que el
 * formato vive en un solo lugar. El dinero sigue por formatearCOP.
 *
 * Solo recibe numeros o texto: una fecha (u otro objeto) da '—'. Sin esa puerta, formatearNumero(new Date(x))
 * pintaba los milisegundos y la guardia de la vista quedaba verde (Q #42741); las fechas van por formatearFecha.
 *
 * @param {number|string|null|undefined} n
 * @param {Intl.NumberFormatOptions} [opciones] las mismas de toLocaleString
 * @param {string} [locale] 'es-CO' por defecto
 * @returns {string} '—' si no hay numero (nunca "NaN" ni milisegundos en pantalla)
 */
export function formatearNumero(n, opciones = {}, locale = 'es-CO') {
  if (n === null || n === undefined || n === '') return '—';
  if (typeof n !== 'number' && typeof n !== 'string') return '—';
  const v = Number(n);
  if (isNaN(v)) return '—';
  return v.toLocaleString(locale, opciones);
}
